import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { useAuth } from "@/hooks/use-auth";
import { subscribeTourEvents } from "@/lib/tour-events";

export type SetupStepId =
  | "business-details"
  | "create-first-quote"
  | "send-quote"
  | "get-paid"
  | "accept-payments";

export interface DemoDataRefs {
  customerIds: number[];
  productIds: number[];
  costIds: number[];
  projectIds: number[];
  templateIds: number[];
  quoteIds: number[];
  invoiceIds: number[];
}

export interface SetupGuideState {
  /** Worked out by the server from the account's own records on every fetch. */
  completedSteps: SetupStepId[];
  dismissedSteps: SetupStepId[];
  dismissed: boolean;
  seenPlan: "free" | "pro";
  shownAt: string | null;
  plan: "free" | "pro";
  steps: SetupStepId[];
  newProStepsAvailable: boolean;
  welcomeDone: boolean;
  // Walkthrough state (mirrors backend)
  tourActive: boolean;
  tourStep: number;
  demoData: DemoDataRefs | null;
  hasDemoData: boolean;
  latestQuoteId: number | null;
  latestInvoiceId: number | null;
}

interface SetupGuideContextType {
  guideState: SetupGuideState | null;
  isOpen: boolean;
  loading: boolean;
  openGuide: () => void;
  closeGuide: () => void;
  refresh: () => Promise<void>;
  dismissStep: (stepId: SetupStepId) => Promise<void>;
  dismissGuide: () => Promise<void>;
  finishWelcome: () => Promise<void>;
  clearDemoData: () => Promise<void>;
  // Walkthrough actions
  startTour: (tourStep: number) => Promise<void>;
  setTourStep: (tourStep: number) => Promise<void>;
  setTourRef: (refs: { quoteId?: number; invoiceId?: number }) => Promise<void>;
  endTour: (opts: { clearDemoData: boolean }) => Promise<void>;
}

const SetupGuideContext = createContext<SetupGuideContextType | null>(null);

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

/** Page events that can tick a checklist item, so the list is re-checked. */
const PROGRESS_EVENTS = new Set([
  "settings.saved",
  "quote.created",
  "quote.sent",
  "invoice.payment-recorded",
]);

async function apiFetch(path: string, opts?: RequestInit) {
  const res = await fetch(`${BASE}/api${path}`, {
    ...opts,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...opts?.headers },
  });
  if (!res.ok) return null;
  return res.json();
}

function patchGuide(body: Record<string, unknown>): Promise<SetupGuideState | null> {
  return apiFetch("/setup-guide", { method: "PATCH", body: JSON.stringify(body) });
}

export function SetupGuideProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [guideState, setGuideState] = useState<SetupGuideState | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const userId = user?.id;
  const userPlan = user?.plan;
  // Field workers join someone else's workspace, so business setup isn't theirs to do.
  const guideApplies = !!user && !user.isAdmin && user.role !== "field";
  const fetchedForRef = useRef<string | null>(null);

  const fetchGuide = useCallback(async () => {
    setLoading(true);
    try {
      const data: SetupGuideState | null = await apiFetch("/setup-guide");
      if (!data) return;

      setGuideState(data);

      if (data.newProStepsAvailable) {
        const updated = await patchGuide({ action: "upgrade-seen" });
        if (updated) {
          setGuideState(updated);
          setIsOpen(true);
        }
        return;
      }

      if (!data.shownAt) {
        // Brand-new users get the welcome form first (rendered separately);
        // the checklist opens once they finish or skip it.
        const updated = await patchGuide({ action: "mark-shown" });
        if (updated) setGuideState(updated);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!guideApplies || !user) {
      setGuideState(null);
      setIsOpen(false);
      fetchedForRef.current = null;
      return;
    }

    const key = `${user.id}:${user.plan}`;
    if (fetchedForRef.current === key) return;
    fetchedForRef.current = key;
    void fetchGuide();
  }, [userId, userPlan, guideApplies]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = useCallback(async () => {
    const data: SetupGuideState | null = await apiFetch("/setup-guide");
    if (data) setGuideState(data);
  }, []);

  const openGuide = useCallback(async () => {
    const data = await patchGuide({ action: "reopen" });
    if (data) setGuideState(data);
    setIsOpen(true);
  }, []);

  const closeGuide = useCallback(() => {
    setIsOpen(false);
  }, []);

  const dismissStep = useCallback(async (stepId: SetupStepId) => {
    const data = await patchGuide({ action: "dismiss-step", stepId });
    if (data) setGuideState(data);
  }, []);

  const dismissGuide = useCallback(async () => {
    const data = await patchGuide({ action: "dismiss" });
    if (data) setGuideState(data);
    setIsOpen(false);
  }, []);

  const finishWelcome = useCallback(async () => {
    const data = await patchGuide({ action: "welcome-done" });
    if (data) setGuideState(data);
    setIsOpen(true);
  }, []);

  const clearDemoData = useCallback(async () => {
    const data = await patchGuide({ action: "clear-demo-data" });
    if (data) setGuideState(data);
  }, []);

  const startTour = useCallback(async (tourStep: number) => {
    const data = await patchGuide({ action: "start-tour", tourStep });
    if (data) setGuideState(data);
    // The walkthrough overlay takes over; tuck the checklist away.
    setIsOpen(false);
  }, []);

  const setTourStep = useCallback(async (tourStep: number) => {
    const data = await patchGuide({ action: "set-tour-step", tourStep });
    if (data) setGuideState(data);
  }, []);

  const setTourRef = useCallback(async (refs: { quoteId?: number; invoiceId?: number }) => {
    const data = await patchGuide({ action: "set-tour-ref", ...refs });
    if (data) setGuideState(data);
  }, []);

  const endTour = useCallback(async (opts: { clearDemoData: boolean }) => {
    const data = await patchGuide({ action: "end-tour", clearDemoData: opts.clearDemoData });
    if (data) setGuideState(data);
    // Back to the checklist, which shows what's left.
    setIsOpen(true);
  }, []);

  // ── Keep the checklist current ────────────────────────────────────────────
  // Progress comes from the server, so re-check after anything that could
  // tick an item, and when the tab regains focus (e.g. back from Stripe).
  const activeRef = useRef(false);
  useEffect(() => {
    activeRef.current = !!guideState && (!guideState.dismissed || guideState.tourActive);
  });

  useEffect(() => {
    if (!guideApplies) return;
    let timer: number | undefined;
    const schedule = () => {
      if (!activeRef.current) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => { void refresh(); }, 400);
    };
    const unsub = subscribeTourEvents((name) => {
      if (PROGRESS_EVENTS.has(name)) schedule();
    });
    const onVisible = () => { if (document.visibilityState === "visible") schedule(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      unsub();
      document.removeEventListener("visibilitychange", onVisible);
      window.clearTimeout(timer);
    };
  }, [guideApplies, refresh]);

  return (
    <SetupGuideContext.Provider value={{
      guideState, isOpen, loading, openGuide, closeGuide, refresh, dismissStep, dismissGuide,
      finishWelcome, clearDemoData, startTour, setTourStep, setTourRef, endTour,
    }}>
      {children}
    </SetupGuideContext.Provider>
  );
}

export function useSetupGuide() {
  const ctx = useContext(SetupGuideContext);
  if (!ctx) throw new Error("useSetupGuide must be used within SetupGuideProvider");
  return ctx;
}
